/**
 * 운영 리포트 목(mock) 데이터
 *
 * ⚠️ 임시 데이터입니다. 서버 API 연동 시 제거합니다.
 * 화면 검증 목적이므로 헬스보이짐 천천점의 실제 강사 이름과
 * 운영 데이터에서 확인된 실제 수치 범위를 사용했습니다.
 */

export type BriefingGrade = 'DANGER' | 'WARNING' | 'CHANCE' | 'PRAISE';

export type BriefingAction = {
    code: string;
    label: string;
    primary?: boolean;
};

/**
 * 추천 액션.
 *
 * 대표님이 어려워하는 건 버튼을 누르는 일이 아니라 "뭐라고 말해야 하나"다.
 * 그래서 무엇을 할지(headline) + 왜(reason) + 실제 보낼 문구(draft)까지 제안한다.
 */
export type Recommendation = {
    /** 무엇을 할지 */
    headline: string;
    /** 왜 지금 이걸 해야 하는지 (한 줄) */
    reason: string;
    /** 그대로 보낼 수 있는 문구 초안 */
    draft: string;
    /** 이 추천을 실행하는 액션 코드 */
    actionCode: string;
    /** 실행 버튼 라벨 */
    actionLabel: string;
};

/**
 * 우측 지표 패널 — 변화를 "얼마나 달라졌는지"로 보여주는 영역.
 *
 * 단일 값(48%)보다 이전 → 현재 대비가 훨씬 빨리 읽힌다.
 * 그래서 before/after 를 나란히 놓고 변화율을 배지로 강조한다.
 */
export type KeyMetric = {
    label: string;
    /** 이전 구간 */
    beforeValue: string;
    beforeLabel: string;
    /** 현재 구간 */
    afterValue: string;
    afterLabel: string;
    /** 변화율(%) — 음수면 감소 */
    deltaPct: number;
    /** 주차별 추이 (최근 8주) */
    trend: number[];
    /** 나쁜 방향 변화인가 */
    alert?: boolean;
};

/**
 * 행동의 재무적 가치.
 *
 * 오너는 "회원이 이탈합니다"보다 "80만 원을 지켜야 합니다"에 훨씬 강하게 반응한다.
 * 다만 근거 없는 금액은 신뢰를 깎으므로 반드시 산출식을 함께 보여준다.
 */
/**
 * 🔴 계약 타입(`types/opsBriefing.ValueImpact`)과 **같은 모양**이어야 합니다.
 *    목이 다른 모양을 들고 있으면 목으로 본 화면이 실서버와 다르게 그려집니다 —
 *    아래 `BriefingItem.value` 주석의 사고가 정확히 그것이었습니다.
 */
export type ValueImpact = {
    /** 미사용 선수금(원) — 잔여 회차 × 회당 단가. 못 세면 null */
    prepaid: number | null;
    /** 재등록 기대(원) — 다음 수강권 예상액 × 재등록률. 기대하지 않는 자리면 0 */
    expected: number | null;
    /** 산출 근거 — 툴팁으로 노출 */
    breakdown: string;
};

export type BriefingItem = {
    itemId: string;
    subjectType: 'MEMBER' | 'COACH' | 'CENTER';
    subjectName: string;
    /** 회원 항목일 때 담당 강사 */
    relatedCoachName?: string;
    grade: BriefingGrade;
    /** 판단 문장 — 화면의 주인공 */
    title: string;
    /** 근거 — 라벨/값 쌍으로 표시 */
    facts: { label: string; value: string; alert?: boolean }[];
    /** 우측 지표 패널 */
    keyMetric: KeyMetric;
    /** 보조 지표 (계약 등) */
    subMetrics?: { label: string; value: string; alert?: boolean }[];
    /**
     * 이 항목의 재무적 가치.
     *
     * ─────────────────────────────────────────────────────────────
     * 🔴 **회원 항목은 여기를 null 로 둔다.** 목이 금액을 적으면 안 된다.
     * ─────────────────────────────────────────────────────────────
     * 계약(types/opsBriefing.ts)이 「서버는 계산하지 마세요. null 로 주시면
     * 됩니다」라고 정해 뒀고, 실서버는 그대로 null 을 보냅니다.
     * 그런데 이 목만 금액을 **적어 두고** 있었습니다. 결과는:
     *
     *   목 화면   → 금액이 잘 뜬다 (목이 적어 준 값)
     *   실서버    → 금액이 늘 비어 있다 (아무도 계산을 안 함)
     *
     * 그래서 「오늘의 브리핑에 금액이 안 뜬다」는 버그가 **목으로 QA 하는 내내
     * 안 보였습니다.** 원인을 서버로 짚어 두기까지 했는데 서버 잘못이 아니었습니다.
     *
     * 게다가 적혀 있던 산출식이 「회당 5만 원 × 전환 가능성 50%」였습니다 —
     * memberValue.ts 가 「근거 없는 임의값」이라고 폐기한 바로 그 숫자입니다.
     * 목이 폐기된 계산을 붙들고 있으면 목으로 본 화면이 거짓말을 합니다.
     *
     * 이제 목도 실서버처럼 **원본(`membership`)만 주고** 금액은 화면이
     * `briefingValue.withValues` 로 계산합니다. 목과 실서버가 같은 경로를 탑니다.
     *
     * (강사·센터 항목은 원래 금액이 없어 그대로 null 입니다)
     */
    value: ValueImpact | null;
    /**
     * 금액 계산 원본. 회원 항목이 아니면 없다.
     *
     * ⚠️ 여기 숫자는 회원 시드(tabData.memberSeeds)와 **같아야 합니다.**
     *    다르면 같은 회원이 오늘의 브리핑과 회원 현황에서 다른 금액으로 뜹니다.
     */
    membership?: {
        membershipId: string;
        product: string;
        totalSession: number | null;
        remain: number | null;
        expiryDday: number | null;
        pricePerSchedule: number | null;
        totalPrice: number | null;
        sessionless?: boolean;
    } | null;
    /** 추천 액션 — 화면에서 가장 강조되는 영역 */
    recommendation: Recommendation;
    /** 대안 액션 */
    actions: BriefingAction[];
    status: 'PENDING' | 'ACTED' | 'DISMISSED';
};

export type YesterdayOrder = {
    orderId: string;
    subjectName: string;
    actionLabel: string;
    done: boolean;
    doneAt?: string;
};

export const GRADE_LABEL: Record<BriefingGrade, string> = {
    DANGER: '위험',
    WARNING: '주의',
    CHANCE: '기회',
    PRAISE: '칭찬',
};

/** 오늘의 브리핑 항목 — 회원 4 + 강사 1 */
export const mockBriefingItems: BriefingItem[] = [
    {
        itemId: 'bi_1',
        subjectType: 'MEMBER',
        subjectName: '이하늘',
        relatedCoachName: '김한솔',
        grade: 'DANGER',
        title: '수강권 소진 속도가 절반으로 떨어졌습니다',
        facts: [
            { label: '최근 60일 수업', value: '6회', alert: true },
            { label: '이전 60일', value: '13회' },
            { label: '마지막 수업', value: '9일 전' },
        ],
        keyMetric: {
            // 4d 검증 결과 — 유일하게 살아남은 주력 신호는 '소진 페이스'
            label: '수강권 소진 페이스',
            beforeLabel: '이전 60일',
            beforeValue: '13회',
            afterLabel: '최근 60일',
            afterValue: '6회',
            deltaPct: -54,
            trend: [3, 3, 2, 2, 1, 1, 1, 0],
            alert: true,
        },
        subMetrics: [
            { label: '잔여', value: '4회', alert: true },
            { label: '만료', value: 'D-11', alert: true },
        ],
        /* 실서버와 같게 — 원본만 주고 금액은 화면이 계산한다 (m1 이하늘 시드와 동일) */
        value: null,
        membership: {
            membershipId: 'm1-1',
            product: 'PT 20회',
            totalSession: 20,
            remain: 4,
            expiryDday: 11,
            pricePerSchedule: 50000,
            totalPrice: 1000000,
        },
        recommendation: {
            headline: '김한솔 강사가 이번 주 안에 연락하게 하세요',
            reason: '만료 2주 전이 재등록을 꺼낼 수 있는 마지막 구간입니다',
            draft:
                '강사님, 이하늘 회원 요즘 좀 뜸하네요. 잔여 4회에 만료가 11일 남았어요.\n이번 주에 한번 연락해보시겠어요? 시간이 안 맞는 거면 옮겨드려도 된다고 해주세요.',
            actionCode: 'NOTIFY_COACH',
            actionLabel: '이대로 전달하기',
        },
        actions: [
            { code: 'NOTIFY_COACH', label: '강사에게 전달', primary: true },
            { code: 'GIFT', label: '선물 보내기' },
            { code: 'MEMO', label: '메모' },
        ],
        status: 'PENDING',
    },
    {
        itemId: 'bi_2',
        subjectType: 'MEMBER',
        subjectName: '박서준',
        relatedCoachName: '조은오',
        grade: 'DANGER',
        title: '수업이 완전히 멈췄고 노쇼도 늘었습니다',
        facts: [
            { label: '최근 60일 수업', value: '3회', alert: true },
            { label: '노쇼율', value: '2.8% (센터 0.9%)', alert: true },
            { label: '마지막 수업', value: '19일 전' },
        ],
        keyMetric: {
            label: '수강권 소진 페이스',
            beforeLabel: '이전 60일',
            beforeValue: '13회',
            afterLabel: '최근 60일',
            afterValue: '3회',
            /* 13 → 3 회는 -77% 다. 같은 회원(박서준)의 회원 현황 시드와 같은 값이어야
             * 두 화면이 같은 변화율을 말한다 (tabData.memberSeeds m2) */
            deltaPct: -77,
            trend: [3, 3, 2, 1, 1, 0, 0, 0],
            alert: true,
        },
        subMetrics: [
            { label: '잔여', value: '2회', alert: true },
            { label: '만료', value: 'D-6', alert: true },
        ],
        /* 실서버와 같게 — 원본만 준다 (m2 박서준 시드와 동일) */
        value: null,
        membership: {
            membershipId: 'm2-1',
            product: 'PT 15회',
            totalSession: 15,
            remain: 2,
            expiryDday: 6,
            pricePerSchedule: 55000,
            totalPrice: 825000,
        },
        recommendation: {
            headline: '이번은 대표님이 직접 연락해 주세요',
            reason: '19일째 공백에 만료가 6일 남았습니다. 강사를 거치면 늦습니다',
            draft:
                '회원님 안녕하세요, 헬스보이짐입니다.\n요즘 통 못 뵈어서 연락드렸어요. 남은 수업이 2회 있는데 편하신 시간으로 잡아드릴까요?\n혹시 불편하셨던 점 있으면 편하게 말씀해 주세요.',
            actionCode: 'CALL',
            actionLabel: '연락하고 완료 체크',
        },
        actions: [
            { code: 'NOTIFY_COACH', label: '강사에게 전달', primary: true },
            { code: 'CALL', label: '직접 연락' },
            { code: 'MEMO', label: '메모' },
        ],
        status: 'PENDING',
    },
    {
        itemId: 'bi_3',
        subjectType: 'COACH',
        subjectName: '성현',
        grade: 'WARNING',
        title: '성과는 좋은데 약속이 흔들립니다',
        facts: [
            { label: '7월 수업', value: '182회' },
            { label: '활성 회원당', value: '14.0회 (평균 11.2회)' },
            { label: '예약 응답', value: '평균 19시간', alert: true },
            { label: '미응답 요청', value: '2건', alert: true },
        ],
        keyMetric: {
            label: '당일 취소',
            beforeLabel: '이전 4주',
            beforeValue: '3건',
            afterLabel: '최근 4주',
            afterValue: '11건',
            deltaPct: 267,
            trend: [1, 0, 1, 1, 2, 3, 3, 3],
            alert: true,
        },
        subMetrics: [{ label: '담당 회원', value: '14명' }],
        /*
         * 🔴 강사 항목은 실서버와 같이 **null** 입니다.
         *    여기 「잠재적 환불 리스크 450만 원」(옛 kind: 'RISK')이 적혀 있어서
         *    목으로 QA 할 때만 헤더에 「잠재 리스크」가 떴습니다. 실서버는 강사 항목의
         *    value 를 null 로 보내므로 운영 화면에서는 늘 0원이었습니다 —
         *    죽은 지표를 살아 있는 것처럼 보이게 한 것이 이 목이었습니다 (정책 M11).
         */
        value: null,
        recommendation: {
            headline: '지적보다 일정 점검으로 먼저 물어보세요',
            reason:
                '활성 회원 13명에게 182회, 회원당 14회로 센터 평균(11.2회)보다 많이 뛰고 있습니다. 태도보다 과부하일 가능성이 큽니다',
            draft:
                '강사님, 이번 달 수업 많으셨죠. 고생 많으십니다.\n다만 당일 취소가 좀 늘어서요. 일정이 빡빡하신 건 아닌지 여쭤보고 싶었어요.\n조정 필요하시면 편하게 말씀해 주세요.',
            actionCode: 'MEETING',
            actionLabel: '면담 요청 보내기',
        },
        actions: [
            { code: 'MEETING', label: '면담 예약', primary: true },
            { code: 'VIEW', label: '상세 보기' },
        ],
        status: 'PENDING',
    },
    {
        itemId: 'bi_4',
        subjectType: 'MEMBER',
        subjectName: '최민서',
        relatedCoachName: '김지원',
        grade: 'CHANCE',
        title: '재등록 이야기를 꺼낼 시점입니다',
        facts: [
            { label: '다닌 기간', value: '6개월' },
            { label: '최근 60일 수업', value: '14회' },
            { label: '노쇼', value: '0회' },
        ],
        keyMetric: {
            label: '수강권 소진 페이스',
            beforeLabel: '이전 60일',
            beforeValue: '13회',
            afterLabel: '최근 60일',
            afterValue: '14회',
            deltaPct: 8,
            trend: [3, 3, 4, 3, 3, 4, 3, 4],
        },
        subMetrics: [
            { label: '잔여', value: '3회', alert: true },
            { label: '만료', value: 'D-18' },
        ],
        /* 실서버와 같게 — 원본만 준다 (m7 최민서 시드와 동일) */
        value: null,
        membership: {
            membershipId: 'm7-3',
            product: 'PT 30회',
            totalSession: 30,
            remain: 3,
            expiryDday: 18,
            pricePerSchedule: 48000,
            totalPrice: 1440000,
        },
        recommendation: {
            headline: '김지원 강사가 다음 수업 때 자연스럽게 꺼내게 하세요',
            reason: '잔여 3회 · 6개월 개근입니다. 지금이 가장 말 꺼내기 좋은 때입니다',
            draft:
                '강사님, 최민서 회원 잔여 3회 남았어요.\n6개월 동안 거의 안 빠지신 분이라 이어가실 것 같은데,\n다음 수업 때 편하게 한번 얘기 꺼내주시겠어요?',
            actionCode: 'NOTIFY_COACH',
            actionLabel: '상담 지시 보내기',
        },
        actions: [
            { code: 'NOTIFY_COACH', label: '상담 지시', primary: true },
            { code: 'MEMO', label: '메모' },
        ],
        status: 'PENDING',
    },
    {
        itemId: 'bi_5',
        subjectType: 'COACH',
        subjectName: '김한솔',
        grade: 'PRAISE',
        title: '이번 달 수업일지를 하나도 빠뜨리지 않았어요',
        facts: [
            { label: '작성', value: '140회 전부' },
            { label: '센터 평균', value: '48%' },
            { label: '담당 회원 이탈', value: '0명' },
        ],
        keyMetric: {
            label: '수업일지 작성률',
            beforeLabel: '센터 평균',
            beforeValue: '48%',
            afterLabel: '김한솔 강사',
            afterValue: '100%',
            deltaPct: 108,
            trend: [88, 92, 95, 97, 100, 100, 100, 100],
        },
        subMetrics: [{ label: '담당 회원', value: '12명' }],
        /* 강사 항목은 금액이 없다 — 실서버와 같게 null (위 주석 참고) */
        value: null,
        recommendation: {
            headline: '짧게라도 직접 한마디 해주세요',
            reason: '돈이 들지 않는 관리입니다. 인정받은 강사가 오래 남습니다',
            draft:
                '강사님, 이번 달 수업일지 140회 전부 쓰셨더라고요.\n센터 평균이 48%인데 정말 대단하십니다.\n회원들도 그만큼 챙김받는다고 느낄 거예요. 고맙습니다.',
            actionCode: 'PRAISE',
            actionLabel: '칭찬 전하기',
        },
        actions: [
            { code: 'PRAISE', label: '칭찬 전하기', primary: true },
            { code: 'VIEW', label: '상세 보기' },
        ],
        status: 'PENDING',
    },
];

/** 어제 지시한 것 */
export const mockYesterdayOrders: YesterdayOrder[] = [
    { orderId: 'yo_1', subjectName: '한수경 회원', actionLabel: '김한솔 강사에게 전달', done: true, doneAt: '오늘 08:12' },
    { orderId: 'yo_2', subjectName: '장윤문 회원', actionLabel: '선물 발송', done: false },
    { orderId: 'yo_3', subjectName: '강도경 회원', actionLabel: '상담 지시', done: false },
];

/** 강사 2×2 */
export const mockCoachMatrix = {
    highPerfGood: ['김한솔', '조은오', '김이황', '강혜빈', '한은지'],
    highPerfWarning: ['성현'],
    lowPerfGood: ['정하림'],
    lowPerfWarning: [] as string[],
    totalCoaches: 17,
};

/** 어제 요약 — 매출은 v1에서 제외(2차 인증 회피) */
export const mockYesterdaySummary = {
    lessons: 38,
    lessonsDeltaPct: -3.2,
    noshows: 2,
    newMembers: 1,
    newMembersDeltaPct: null as number | null,
};

/** 신규 센터 온보딩 진행률 — null 이면 표시하지 않음 */
export const mockOnboardingProgress: { dayCount: number; totalDays: number; nextUnlockLabel: string } | null = null;
